import React, { ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'filter' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  type = 'button',
  disabled = false,
  onClick,
  className = '',
  style = {},
  title,
  ...rest
}: ButtonProps) {
  // Variant mapping
  const variantClassMap: Record<string, string> = {
    primary: 'glass-button',
    secondary: 'glass-button-secondary',
    tertiary: 'glass-button-tertiary',
    filter: 'glass-button-filter',
    danger: 'glass-button glass-button-danger',
    ghost: 'glass-button-ghost'
  };

  // Size mapping
  const sizeStyles: Record<string, React.CSSProperties> = {
    sm: { padding: '4px 12px', fontSize: '13px' },
    md: { padding: '6px 16px', fontSize: '14px' },
    lg: { padding: '10px 24px', fontSize: '16px' }
  };

  const isButtonDisabled = disabled || isLoading;
  const baseClass = variantClassMap[variant] || 'glass-button';
  const combinedClassName = `${baseClass} ${className}`.trim();

  return (
    <button
      type={type}
      disabled={isButtonDisabled}
      onClick={onClick}
      className={combinedClassName}
      style={{
        ...sizeStyles[size],
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        opacity: isButtonDisabled ? 0.65 : 1,
        cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-work-sans, sans-serif)',
        borderRadius: variant === 'filter' ? 'var(--radius-round, 1000px)' : 'var(--radius-xs, 4px)',
        ...style
      }}
      title={title}
      aria-busy={isLoading}
      aria-disabled={isButtonDisabled}
      {...rest}
    >
      {isLoading ? (
        <Loader2 size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} className="animate-spin" />
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
}
